import { Directive, HostListener, OnInit } from '@angular/core';
import { ProductListing } from '@df/catalog-ui/listing/product-listing';
import { ProductComponentDef } from '@df/catalog-ui/product/product.component.def';
import { IProductDataV2, Product } from '@df/catalog/product/product';
import { Nexus } from '@tomandco/nexus-api-types';
import findKey from 'lodash-es/findKey';
import isEmpty from 'lodash-es/isEmpty';
import take from 'lodash-es/take';
import without from 'lodash-es/without';
import { Subscription } from 'rxjs';

@Directive()
export class ProductComponentCrosssellsDef<T extends Product<IProductDataV2> = Product<IProductDataV2>>
    extends ProductComponentDef<T>
    implements OnInit
{
    /**
     * Attributes allowed to be use with swatches
     */
    protected swatchAttributes = ['color'];

    /**
     * Attribute used with swatches
     */
    protected swatchAttribute?: string;

    /**
     * Sets current swatch selecttion
     */
    currentSwatchSelection?: number;

    /**
     * If linked parents has been loaded
     */
    private linkedParentsLoaded = false;

    /**
     * If user interacted with this component
     */
    interacted = false;

    /**
     * Min number of swatches to display them
     */
    protected minSwatches = 0;

    /**
     * Override to true if you need to auto select swatch on filter change
     */
    protected autoPickSwatchOnFilter = false;

    /**
     * Fitlter selection
     */
    protected listingFilterSubscription?: Subscription;

    /**
     * Parent matching current swatch selection
     */
    private _currentParent: T | undefined;

    get currentParent(): T | undefined {
        return (this._currentParent?.data && this._currentParent) || (this.parent?.data && this.parent) || (this.model?.data && this.model);
    }

    set currentParent(model: T | undefined) {
        if (this._currentParent !== model) {
            this._currentParent = model;
            this.onCurrentParentChange();
        }
    }

    override get price() {
        return this.currentParent!.getPrice({ ...this.request, qty: 1 });
    }

    override get orgPrice() {
        return this.currentParent!.getOrgPrice({ ...this.request, qty: 1 });
    }

    override async ngOnInit(): Promise<void> {
        super.ngOnInit();
        await this.loadLinkedParents();
    }

    override ngOnDestroy(): void {
        this.listingFilterSubscription?.unsubscribe();
        super.ngOnDestroy();
    }

    /**
     * Update swatch selectection on model changes
     */
    override onModelChange() {
        super.onModelChange();
        this.selectSwatchAttribute();
        this.resetSwatch(true);
        this.setListingFilterSubscription();
    }

    protected selectSwatchAttribute() {
        const data = this.parent?.data || this.model.data!;
        this.swatchAttribute =
            (this.parent?.isConfigurable && this.swatchAttributes.find(i => data.options?.configurable?.[i])) ||
            this.swatchAttributes.find(i => data[i])!;
    }

    protected getInitialSwatch(): number | undefined {
        if (this.isCrossSellLinked()) {
            return this.getSwatchByParent(this.parent?.id);
        } else {
            const swatchValue = this.swatchAttribute ? this.model!.data![this.swatchAttribute] : undefined;
            if (typeof swatchValue === 'number') {
                return swatchValue;
            }
        }
        const allSwatches = this.getAllSwatches();
        if (allSwatches?.length) {
            return allSwatches[0];
        }
    }

    protected getAllSwatches(): number[] | undefined {
        const swatches = this.swatchAttribute ? this.parent?.data?.[this.swatchAttribute] : undefined;
        if (typeof swatches === 'number') {
            return [swatches];
        }
        return swatches;
    }

    /**
     * Swatch values
     */
    getSwatches(limit?: number): number[] | undefined {
        const options = this.getAllSwatches();
        let swatches = Array.isArray(options) ? [...options] : undefined;
        if (!swatches || this.minSwatches > swatches.length) {
            return undefined;
        }
        // make sure that initial swatch (prefered one for initial model is first)
        if (swatches) {
            const prioritySwatch = this.getInitialSwatch();
            swatches = prioritySwatch ? [prioritySwatch, ...without(swatches, prioritySwatch)] : swatches;
        }
        // make sure that curently selected swatch is visible (in case some autoamtion picked one from further ones)
        const finalSwatches = swatches && limit ? take(swatches, limit) : swatches;
        if (finalSwatches && this.currentSwatchSelection && !finalSwatches.includes(this.currentSwatchSelection)) {
            finalSwatches.splice(finalSwatches.length - 1, 1, this.currentSwatchSelection);
        }
        return finalSwatches;
    }

    /**
     * Retrieves number of swatches
     */
    getSwatchesCount(): number {
        return this.getSwatches()?.length || 0;
    }

    /**
     * Is swatch selected
     */
    isSwatchSelected(swatch: number): boolean {
        return this.currentSwatchSelection === swatch;
    }

    getBasketRequest(): Nexus.Lib.Basket.BasketRequestData | undefined {
        // we match skipping other attributes the swatch cause we know by isSimpleDetermined method that we can
        const id = this.getAddableProduct();
        if (!id) {
            return undefined;
        }
        return {
            id,
            qty: 1
        };
    }

    /**
     * Retrieves  product id that can be used in adding to basket
     */
    protected getAddableProduct(): number | undefined {
        const currentParent = this.currentParent;
        if (!currentParent?.data) {
            return;
        }
        if (currentParent.isSimple) {
            return currentParent.id;
        }
        if (currentParent.children.length === 1) {
            return currentParent.children[0].id;
        }
        if (currentParent.isConfigurable) {
            const matchingSimples =
                this.swatchAttribute &&
                this.currentSwatchSelection &&
                currentParent.data.options?.configurable?.[this.swatchAttribute]?.[this.currentSwatchSelection];
            if (matchingSimples && matchingSimples.length === 1) {
                return matchingSimples[0];
            }
        }
        if (currentParent.isBundle) {
            return currentParent.id;
        }
        return undefined;
    }

    /**
     * Updates current swatch
     */
    protected selectSwatch(swatch?: number, silent = false): void {
        if (this.currentSwatchSelection !== swatch) {
            this.currentSwatchSelection = swatch;
            this.onSwatchSelectionChange();

            if (!silent) {
                this.detectChanges();
            }
        }
    }

    protected onSwatchSelectionChange(): boolean {
        let product: T | undefined;
        if (this.linkedParentsLoaded) {
            product = this.getParentBySwatch();
        } else if (this.configurableChildrenLoaded && this.canUseConfigurableChildren()) {
            const children = this.getChildrenMathingSwatch();
            product = children?.[0] || this.originalModel;
        } else {
            product = this.parent;
        }

        if (this.currentParent !== product) {
            this.currentParent = product;
            return true;
        }
        return false;
    }

    @HostListener('click')
    protected onClick() {
        this.onInteraction();
    }

    @HostListener('mouseenter')
    protected onMouseIn() {
        this.onInteraction();
    }

    /**
     * When user interacts with component we can provide additiona contents
     */
    protected onInteraction() {
        if (!this.interacted) {
            this.interacted = true;

            this.detectChanges();
        }
    }
    /**
     * Retrieves parent model matchin currently selected swatch
     */
    protected getParentBySwatch(swatchId: number | undefined = this.currentSwatchSelection, strict = false): T | undefined {
        const options = this.getSwatchRelatedOptionsMap();

        if (options && swatchId && options[swatchId]) {
            const matchingChildren = options[swatchId];
            const matchingParent =
                this.parent?.data &&
                findKey(this.parent.data.indirectChildren, children => !!children.find(child => matchingChildren.includes(child)));

            if (matchingParent) {
                const parent = this.app.resources.product.getExisting(Number(matchingParent)) as T;
                if (parent) {
                    return parent;
                }
            }
            if (strict) {
                if (this.parent?.directChildrenIds.find(i => matchingChildren.includes(i))) {
                    return this.parent;
                }

                return undefined;
            }
        }

        return this.parent?.data && this.parent;
    }

    /**
     * Retrieves swatch id matching given parent model id
     */
    protected getSwatchByParent(parentId: number | undefined): number | undefined {
        if (!parentId) {
            return undefined;
        }
        const matchingChildren =
            this.parent?.id === parentId ? this.parent?.data?.directChildrenIds : this.parent?.data?.indirectChildren?.[parentId];
        const swatchId =
            matchingChildren &&
            findKey(this.getSwatchRelatedOptionsMap(), children => children.find(child => matchingChildren.includes(child)));
        return swatchId ? Number(swatchId) : undefined;
    }

    private getSwatchRelatedOptionsMap() {
        return this.swatchAttribute ? this.parent?.data?.options?.configurable?.[this.swatchAttribute] : undefined;
    }

    protected getChildrenMathingSwatch(swatchId: number | undefined = this.currentSwatchSelection): T[] | undefined {
        return swatchId && this.swatchAttribute
            ? (this.parent?.matchChildren({ [this.swatchAttribute]: swatchId }, true) as T[] | undefined)
            : undefined;
    }

    /**
     * If product is configuarable family setup
     */
    protected isCrossSellLinked(): boolean {
        return !isEmpty(this.parent?.data?.indirectChildren);
    }

    /**
     * Loads other parents of the familt
     */
    protected async loadLinkedParents(): Promise<void> {
        if (!this.isCrossSellLinked()) {
            return;
        }
        const map = this.parent?.data?.indirectChildren;

        if (!map) {
            return;
        }

        await this.app.resources.product.loadAllIds(
            Object.keys(map).map(i => parseInt(i, 10)),
            2,
            { safe: true }
        );

        this.linkedParentsLoaded = true;

        if (this.initiated) {
            if (this.onSwatchSelectionChange()) {
                this.detectChanges();
            }
        }
    }

    protected override canUseConfigurableChildren(): boolean {
        return super.canUseConfigurableChildren() && !this.isCrossSellLinked();
    }

    protected onCurrentParentChange() {}

    protected override onChildrenLoaded(): void {
        this.onSwatchSelectionChange();
        super.onChildrenLoaded();
    }

    /**
     * SUbscribes to listing filter to auto pick swatches
     */
    protected setListingFilterSubscription() {
        this.listingFilterSubscription?.unsubscribe();
        const listing = this.listing;
        if (this.autoPickSwatchOnFilter && listing && this.swatchAttribute) {
            const onListingChange = (listing: ProductListing<T>) => {
                const filter = this.swatchAttribute ? listing.filtersMap[this.swatchAttribute] : undefined;
                if (filter) {
                    const selection = filter.selection;

                    if (selection?.length) {
                        const swatch = this.getSwatchFromFilterSelection(selection);
                        if (swatch) {
                            this.selectSwatch(swatch);
                        }
                    } else {
                        this.resetSwatch();
                    }
                }
            };

            this.listingFilterSubscription = listing.subscribe(() => {
                onListingChange(listing);
            });
            onListingChange(listing);
        }
    }

    protected resetSwatch(silent = false): void {
        this.selectSwatch(this.getInitialSwatch(), silent);
    }

    protected getSwatchFromFilterSelection(selection: number[]): number | undefined {
        return this.getAllSwatches()?.find(i => selection.includes(i));
    }
}
